import { useId, useRef, type ComponentType, type MutableRefObject, type ReactNode } from 'react';
import clsx from 'clsx';
import {
  AlignLeft, ArrowDown, ArrowUp, Building2, Calendar, CalendarClock, CheckSquare, CircleDot, Clock, Hash, LayoutList, ListChecks, Network, Paperclip, Plus, Trash2, Type, User, UsersRound,
} from 'lucide-react';
import {
  FORM_FIELD_LABELS, canMoveRow, canRemoveSectionRow, fieldIdFromLabel, formSectionsOfRows, insertItemRow, insertSectionRow, isPickerField, moveRow, removeRow,
  rowsOf, rowsOfFormSections, sectionFields, splitRows,
  type BuilderRow, type FormField, type FormFieldType, type FormSection, type FormValue, type FormValues, type SectionRowData,
} from '@tradeoutlet/shared';
import { Button, Input, Select, Textarea, Toggle } from '@/components/ui';
import { FormFieldInput, FormFieldsEditor, type FormUpload } from './FormFields';

/** t635u: one icon per field type, used by the Add field sidebar / sheet and on each field card. */
export const FIELD_TYPE_ICONS: Record<FormFieldType, ComponentType<{ className?: string }>> = {
  short_text: Type, long_text: AlignLeft, number: Hash, single_select: CircleDot, multi_select: ListChecks, date: Calendar, time: Clock,
  datetime: CalendarClock, checkbox: CheckSquare, file: Paperclip, person: User, building: Building2,
  department: Network, group: UsersRound,
};

/** A new, empty field of `type` (t635u: no label yet - the card shows a "Question" placeholder; the internal name follows the label). */
export function newField(type: FormFieldType, taken: readonly string[], label = ''): FormField {
  return {
    id: fieldIdFromLabel(label || FORM_FIELD_LABELS[type], taken), label, type,
    ...(type === 'single_select' || type === 'multi_select' ? { options: ['Option 1', 'Option 2'] } : {}),
  };
}

/** What a typed internal name can hold: lower-case letters, digits and underscores, starting with a letter. */
export const cleanInternalName = (v: string) => v.toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^[^a-z]+/, '').slice(0, 40);

type BuilderIcon = ComponentType<{ className?: string }>;
/** t721u: one choice in a builder's Add list (a field type, a checklist item type, ...). */
export interface BuilderAddOption<K extends string = string> { key: K; label: string; icon: BuilderIcon }

/** t721u: the shared Add list of a template builder, as a vertical list (desktop sidebar) or a grid (phone sheet). */
export function BuilderTypePicker<K extends string>({ options, onPick, layout = 'list', label = 'Field types', testIdPrefix = 'add-field' }: {
  options: readonly BuilderAddOption<K>[]; onPick: (k: K) => void; layout?: 'list' | 'grid'; label?: string; testIdPrefix?: string;
}) {
  return (
    <ul aria-label={label} className={clsx(layout === 'grid' ? 'grid grid-cols-2 gap-1' : 'space-y-0.5')}>
      {options.map(({ key, label: text, icon: Icon }) => (
        <li key={key}>
          <button type="button" onClick={() => onPick(key)} data-testid={`${testIdPrefix}-${key}`}
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line dark:text-slate-200 dark:hover:bg-slate-800">
            <Icon className="h-4 w-4 shrink-0 text-slate-500" />{text}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** t725u: the "Section" item at the top of every builder's Add list: adds a section heading after the selection. */
export function BuilderSectionButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} data-testid="add-section"
      className="mb-2 flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line dark:text-slate-200 dark:hover:bg-slate-800">
      <LayoutList className="h-4 w-4 shrink-0 text-slate-500" />Section
    </button>
  );
}

/** The list of field types to add, as a vertical list (desktop sidebar) or a grid (phone sheet). */
export function FieldTypePicker({ types, onPick, layout = 'list' }: { types: readonly FormFieldType[]; onPick: (t: FormFieldType) => void; layout?: 'list' | 'grid' }) {
  return <BuilderTypePicker options={types.map(t => ({ key: t, label: FORM_FIELD_LABELS[t], icon: FIELD_TYPE_ICONS[t] }))} onPick={onPick} layout={layout} />;
}

/**
 * t721u: one numbered card in a template builder (form fields, checklist items): the number in a left gutter, a header
 * row (icon, label, type, move and remove) and, when open, the card's settings. Clicking or focusing it selects it.
 */
export function BuilderCard({ number, selected, onSelect, header, children, testId = 'builder-field' }: {
  number: number; selected?: boolean; onSelect?: () => void; header: ReactNode; children?: ReactNode; testId?: string;
}) {
  const pick = () => { if (onSelect && !selected) onSelect(); };
  return (
    // t638u: the number sits outside the card in a left gutter, aligned to the card's first row, so fields read as a numbered list.
    <div className="flex items-start gap-1.5 sm:gap-2" data-testid={testId}>
      <span data-testid="field-number" aria-hidden className={clsx('w-5 shrink-0 pt-[0.95rem] text-right text-sm tabular-nums sm:w-7', onSelect && selected ? 'font-semibold text-slate-800 dark:text-slate-100' : 'text-slate-500')}>{number}</span>
      <div className={clsx('min-w-0 flex-1 rounded-xl border bg-white shadow-sm dark:bg-slate-900', onSelect && selected ? 'border-accent-line ring-1 ring-accent-line' : 'border-slate-200 dark:border-slate-800')}
        onFocusCapture={pick} onMouseDown={pick}>
        <div className={clsx('flex flex-wrap items-center gap-2 px-4 py-2', children && 'border-b border-slate-100 dark:border-slate-800')}>{header}</div>
        {children && <div className="grid gap-3 px-4 py-3 text-xs sm:grid-cols-2">{children}</div>}
      </div>
    </div>
  );
}

/** t721u: a builder card's Move up / Move down / Remove buttons. */
export function BuilderCardMoves({ name, index, count, onMove, onRemove, removeTitle = 'Remove field', canUp, canDown }: {
  name: string; index: number; count: number; onMove: (d: -1 | 1) => void; onRemove: () => void; removeTitle?: string;
  /** t725u: override the first / last checks (rows with section headings). */
  canUp?: boolean; canDown?: boolean;
}) {
  return <>
    <button type="button" title="Move up" aria-label={`Move ${name} up`} disabled={!(canUp ?? index > 0)} onClick={() => onMove(-1)} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"><ArrowUp className="h-4 w-4" /></button>
    <button type="button" title="Move down" aria-label={`Move ${name} down`} disabled={!(canDown ?? index < count - 1)} onClick={() => onMove(1)} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"><ArrowDown className="h-4 w-4" /></button>
    <button type="button" title={removeTitle} aria-label={`Remove ${name}`} onClick={onRemove} className="rounded p-1 text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
  </>;
}

/** t721u: the muted, labelled "Internal name" at the foot of a builder card (fixed once saved). */
export function InternalNameField({ ariaLabel, value, saved, hint, onChange, onBlur, onReset }: {
  ariaLabel: string; value: string; saved: boolean; hint: string; onChange: (v: string) => void; onBlur?: () => void;
  /** Shown as "Match the question" when the name was typed by hand. */
  onReset?: () => void;
}) {
  return (
    <div className="sm:col-span-2" data-testid="internal-name">
      <label className="block text-[11px] font-medium text-slate-500">Internal name
        <Input aria-label={ariaLabel} className="mt-1 h-7 max-w-xs font-mono text-xs text-slate-600 dark:text-slate-300" value={value} readOnly={saved} spellCheck={false}
          title={saved ? 'Fixed once saved, so earlier answers still match' : undefined}
          onChange={e => onChange(e.target.value)} onBlur={onBlur} />
      </label>
      <p className="mt-0.5 text-[11px] text-slate-500">
        {hint}{saved ? '. Fixed once saved.' : ''}
        {!saved && onReset && <> · <button type="button" className="underline underline-offset-2 hover:text-slate-700 dark:hover:text-slate-300" onClick={onReset}>Match the question</button></>}
      </p>
    </div>
  );
}

/**
 * t725u: a section heading in a builder (every builder: channel forms, checklists, handover templates, issue forms):
 * title and optional description, moved with the fields under it, removed without losing them (they join the section
 * above). No gutter number: only questions are numbered.
 */
export function SectionHeaderCard({ number, section, selected, onSelect, onChange, onMove, canUp, canDown, onRemove, canRemove = true }: {
  /** Which section this is (1 = the first), for labels. */
  number: number; section: SectionRowData; selected?: boolean; onSelect?: () => void; onChange: (s: SectionRowData) => void;
  onMove: (d: -1 | 1) => void; canUp: boolean; canDown: boolean; onRemove: () => void; canRemove?: boolean;
}) {
  const pick = () => { if (onSelect && !selected) onSelect(); };
  const name = section.title.trim() || `section ${number}`;
  return (
    <div className="flex items-start gap-1.5 sm:gap-2" data-testid="section-header">
      <span aria-hidden className="w-5 shrink-0 sm:w-7" />
      <div className={clsx('min-w-0 flex-1 space-y-1.5 rounded-xl border bg-slate-50 px-4 py-2.5 dark:bg-slate-800/60', onSelect && selected ? 'border-accent-line ring-1 ring-accent-line' : 'border-slate-200 dark:border-slate-700')}
        onFocusCapture={pick} onMouseDown={pick}>
        <div className="flex items-center gap-2">
          <Input aria-label={`Section ${number} title`} width="full" className="h-9 flex-1 text-base font-semibold" value={section.title} placeholder="Section title" onChange={e => onChange({ ...section, title: e.target.value })} />
          <button type="button" title="Move section up" aria-label={`Move section ${name} up`} disabled={!canUp} onClick={() => onMove(-1)} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"><ArrowUp className="h-4 w-4" /></button>
          <button type="button" title="Move section down" aria-label={`Move section ${name} down`} disabled={!canDown} onClick={() => onMove(1)} className="rounded p-1 text-slate-500 hover:bg-slate-100 disabled:opacity-30 dark:hover:bg-slate-800"><ArrowDown className="h-4 w-4" /></button>
          <button type="button" title={canRemove ? 'Remove section (its fields stay)' : 'Move these fields under another section first'} aria-label={`Remove section ${name}`} disabled={!canRemove} onClick={onRemove}
            className="rounded p-1 text-slate-500 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-500"><Trash2 className="h-4 w-4" /></button>
        </div>
        <Input aria-label={`Section ${number} description`} width="full" className="h-8 text-sm" placeholder="Description (optional)" value={section.description ?? ''} onChange={e => onChange({ ...section, description: e.target.value || null })} />
      </div>
    </div>
  );
}

/** What a builder row renderer gets: its gutter number, selection and the shared Move / Remove buttons. */
export interface BuilderItemProps {
  row: number; number: number; selected: boolean; select: (() => void) | undefined;
  moves: (name: string, removeTitle?: string) => ReactNode;
}

/**
 * t725u: the rows of a builder - questions (fields, checklist items) and section headings - in order. Each section is a
 * region holding its heading and the questions under it; numbers run on across sections. `nested`: every question
 * belongs to a section (handover templates, issue forms), so none can move above the first one.
 */
export function BuilderRowsList<T>({ rows, onChange, selected, onSelect, nested, renderItem, onRemoveItem, sectionFooter, emptySection }: {
  rows: BuilderRow<T>[]; onChange: (rows: BuilderRow<T>[]) => void;
  /** The selected row (index into rows). */
  selected?: number | null; onSelect?: (i: number | null) => void; nested?: boolean;
  renderItem: (item: T, p: BuilderItemProps) => ReactNode;
  onRemoveItem?: (item: T) => void;
  /** Shown under a section's questions (e.g. inline Add buttons); gets the row after which to add. */
  sectionFooter?: (lastRow: number) => ReactNode;
  /** Shown in a section with no questions yet. */
  emptySection?: string;
}) {
  const uid = useId();
  const selectable = Boolean(onSelect);
  const move = (i: number, d: -1 | 1) => {
    const r = moveRow(rows, i, d, nested);
    onChange(r.rows);
    if (selectable) onSelect!(r.index);
  };
  const remove = (i: number) => {
    const r = rows[i];
    if (r.kind === 'item') onRemoveItem?.(r.item);
    onChange(removeRow(rows, i));
    if (selected != null && onSelect) onSelect(selected === i ? null : selected > i ? selected - 1 : selected);
  };
  const item = (i: number, number: number) => {
    const r = rows[i] as Extract<BuilderRow<T>, { kind: 'item' }>;
    return (
      <div key={`${uid}-${i}`}>
        {renderItem(r.item, {
          row: i, number, selected: selected === i, select: selectable ? () => onSelect!(i) : undefined,
          moves: (name, removeTitle) => <BuilderCardMoves name={name} index={i} count={rows.length} canUp={canMoveRow(rows, i, -1, nested)} canDown={canMoveRow(rows, i, 1, nested)}
            onMove={d => move(i, d)} onRemove={() => remove(i)} removeTitle={removeTitle} />,
        })}
      </div>
    );
  };
  // Group rows: leading questions (flat lists), then each section with its questions.
  const groups: { head: number | null; rows: number[] }[] = [];
  rows.forEach((r, i) => {
    if (r.kind === 'section') groups.push({ head: i, rows: [] });
    else if (groups.length) groups[groups.length - 1].rows.push(i);
    else groups.push({ head: null, rows: [i] });
  });
  let n = 0; let sectionNo = 0;
  return (
    <div className="space-y-3">
      {groups.map(g => {
        const items = g.rows.map(i => item(i, ++n));
        if (g.head == null) return <div key={`${uid}-lead`} className="space-y-3">{items}</div>;
        const h = g.head;
        const sec = (rows[h] as Extract<BuilderRow<T>, { kind: 'section' }>).section;
        const no = ++sectionNo;
        const last = g.rows.length ? g.rows[g.rows.length - 1] : h;
        return (
          <section key={`${uid}-s${h}`} aria-label={`Section ${no}: ${sec.title}`} data-testid="builder-section" className={clsx('space-y-3', h > 0 && 'pt-2')}>
            <SectionHeaderCard number={no} section={sec} selected={selected === h} onSelect={selectable ? () => onSelect!(h) : undefined}
              onChange={x => onChange(rows.map((r, j) => (j === h ? { kind: 'section', section: x } : r)))}
              onMove={d => move(h, d)} canUp={canMoveRow(rows, h, -1, nested)} canDown={canMoveRow(rows, h, 1, nested)}
              onRemove={() => remove(h)} canRemove={canRemoveSectionRow(rows, h, nested)} />
            {items}
            {!g.rows.length && emptySection && <p className="py-1 pl-[1.625rem] text-sm text-slate-500 sm:pl-9">{emptySection}</p>}
            {sectionFooter?.(last)}
          </section>
        );
      })}
    </div>
  );
}

/**
 * Shared form builder (backlog #13 form channels, #34 handover templates, #36 "Report an issue"): add, remove,
 * reorder and configure fields (type, required, help text, options, bounds, default / pre-filled value).
 * Saved field ids never change, so answers given with older versions still match.
 *
 * t635u: the question label is the main text on each card; the stored key is a separate, muted "Internal name"
 * that follows the label until someone edits it (and is fixed once saved). With `selected`/`onSelect` only the
 * selected card shows its settings; `hideAdd` drops the inline "Add field" row when a sidebar or sheet adds fields.
 * t725u: works on rows (fields and section headings); numbers run on across sections.
 */
export function FieldRowsBuilder({ rows, onChange, types, savedIds, takenIds = [], label = 'Field', selected, onSelect, hideAdd, typedIds, nested }: {
  rows: BuilderRow<FormField>[]; onChange: (rows: BuilderRow<FormField>[]) => void; types: readonly FormFieldType[];
  /** Ids already stored in a saved version: kept even when the label changes. */
  savedIds: ReadonlySet<string>;
  /** Ids used elsewhere, so new ids stay unique form-wide. */
  takenIds?: readonly string[];
  label?: string;
  /** The selected row. */
  selected?: number | null; onSelect?: (i: number | null) => void; hideAdd?: boolean;
  /** Hand-typed internal names, kept by the page so they survive the builder unmounting (e.g. a trip to the Settings tab). */
  typedIds?: MutableRefObject<Set<string>>;
  nested?: boolean;
}) {
  /** Internal names someone typed by hand: they stop following the label. Keyed by id, so reordering keeps them. */
  const ownTyped = useRef(new Set<string>());
  const typed = typedIds ?? ownTyped;
  const fieldOf = (i: number) => (rows[i] as Extract<BuilderRow<FormField>, { kind: 'item' }>).item;
  const others = (i: number) => [...takenIds, ...rows.flatMap((r, j) => (r.kind === 'item' && j !== i ? [r.item.id] : []))];
  const setField = (i: number, f: FormField) => onChange(rows.map((r, j) => (j === i ? { kind: 'item', item: f } : r)));
  const update = (i: number, patch: Partial<FormField>) => {
    const f = fieldOf(i);
    const next = { ...f, ...patch };
    if (patch.label !== undefined && !savedIds.has(f.id) && !typed.current.has(f.id)) next.id = fieldIdFromLabel(patch.label || FORM_FIELD_LABELS[next.type], others(i));
    if (patch.type && patch.type !== f.type) { delete next.default; if ((patch.type === 'single_select' || patch.type === 'multi_select') && !next.options?.length) next.options = ['Option 1', 'Option 2']; }
    setField(i, next);
  };
  const rename = (i: number, raw: string) => {
    const f = fieldOf(i);
    if (savedIds.has(f.id)) return;
    const id = cleanInternalName(raw);
    typed.current.delete(f.id);
    if (id) typed.current.add(id);
    setField(i, { ...f, id: id || f.id });
  };
  /** On blur, a typed internal name that clashes with another field gets a _2 suffix. */
  const settle = (i: number) => {
    const f = fieldOf(i);
    if (savedIds.has(f.id) || !typed.current.has(f.id)) return;
    if (others(i).includes(f.id)) { const id = fieldIdFromLabel(f.id, others(i)); typed.current.delete(f.id); typed.current.add(id); setField(i, { ...f, id }); }
  };
  const resetName = (i: number) => {
    const f = fieldOf(i);
    typed.current.delete(f.id);
    setField(i, { ...f, id: fieldIdFromLabel(f.label || FORM_FIELD_LABELS[f.type], others(i)) });
  };
  const add = (after: number, type: FormFieldType) => onChange(insertItemRow(rows, after, newField(type, [...takenIds, ...rows.flatMap(r => (r.kind === 'item' ? [r.item.id] : []))], FORM_FIELD_LABELS[type])).rows);
  const selectable = Boolean(onSelect);
  const addRow = (after: number) => (
    <div className="flex flex-wrap items-center gap-2 pl-[1.625rem] sm:pl-9">
      <span className="text-xs font-semibold text-slate-500">Add field:</span>
      {types.map(t => <Button key={t} size="sm" variant="secondary" onClick={() => add(after, t)}><Plus className="h-3 w-3" />{FORM_FIELD_LABELS[t]}</Button>)}
    </div>
  );
  const hasSections = rows.some(r => r.kind === 'section');
  return (
    <div className="space-y-3">
      <BuilderRowsList rows={rows} onChange={onChange} selected={selected} onSelect={onSelect} nested={nested}
        onRemoveItem={f => typed.current.delete(f.id)}
        emptySection={selectable ? 'No fields yet. Pick a field type to add one here.' : undefined}
        sectionFooter={!hideAdd ? addRow : undefined}
        renderItem={(f, p) => {
          const i = p.row;
          const open = !selectable || p.selected;
          const saved = savedIds.has(f.id);
          const Icon = FIELD_TYPE_ICONS[f.type] ?? Type;
          return (
            <BuilderCard number={p.number} selected={p.selected} onSelect={p.select}
              header={<>
                <Icon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                <Input aria-label={`${label} ${p.number} label`} width="full" className="h-8 min-w-48 flex-1 font-medium" value={f.label} placeholder="Question" onChange={e => update(i, { label: e.target.value })} />
                <Select aria-label={`${label} ${p.number} type`} className="h-8 w-40" value={f.type} onChange={e => update(i, { type: e.target.value as FormFieldType })}>
                  {types.map(t => <option key={t} value={t}>{FORM_FIELD_LABELS[t]}</option>)}
                </Select>
                {p.moves(f.label.trim() || 'field')}
              </>}>
              {open && <>
                <div className="flex flex-wrap items-center gap-4 sm:col-span-2">
                  <Toggle checked={Boolean(f.required)} onChange={v => update(i, { required: v })} label="Required" />
                  {isPickerField(f.type) && <Toggle checked={Boolean(f.multiple)} onChange={v => update(i, { multiple: v })} label="Allow several" />}
                  {f.type === 'building' && <Toggle checked={Boolean(f.default_own_building)} onChange={v => update(i, { default_own_building: v || undefined })} label="Default to the poster’s building" />}
                </div>
                <label className="font-semibold text-slate-500 sm:col-span-2">Help text<Input className="mt-1 h-8" value={f.help ?? ''} onChange={e => update(i, { help: e.target.value || null })} /></label>
                {(f.type === 'single_select' || f.type === 'multi_select') && (
                  <label className="font-semibold text-slate-500 sm:col-span-2">Options (one per line)
                    <Textarea className="mt-1" rows={Math.min(6, (f.options?.length ?? 1) + 1)} value={(f.options ?? []).join('\n')} onChange={e => update(i, { options: e.target.value.split('\n') })} onBlur={() => update(i, { options: (f.options ?? []).map(o => o.trim()).filter(Boolean) })} />
                  </label>
                )}
                {f.type === 'number' && (
                  <div className="flex gap-2 sm:col-span-2">
                    <label className="font-semibold text-slate-500">Min<Input className="mt-1 h-8 w-24" type="number" value={f.min ?? ''} onChange={e => update(i, { min: e.target.value === '' ? null : Number(e.target.value) })} /></label>
                    <label className="font-semibold text-slate-500">Max<Input className="mt-1 h-8 w-24" type="number" value={f.max ?? ''} onChange={e => update(i, { max: e.target.value === '' ? null : Number(e.target.value) })} /></label>
                    <label className="font-semibold text-slate-500">Unit<Input className="mt-1 h-8 w-32" value={f.unit ?? ''} placeholder="pallets" onChange={e => update(i, { unit: e.target.value || null })} /></label>
                  </div>
                )}
                {f.type === 'file' && <label className="font-semibold text-slate-500">Max files<Input className="mt-1 h-8 w-24" type="number" min={1} max={10} value={f.max ?? 10} onChange={e => update(i, { max: Number(e.target.value) || null })} /></label>}
                {f.type !== 'file' && (
                  <div className="sm:col-span-2"><div className="mb-1 font-semibold text-slate-500">Pre-filled value</div>
                    <FormFieldInput field={{ ...f, required: false }} value={f.default ?? undefined} onChange={(v?: FormValue) => update(i, { default: v ?? null })} />
                  </div>
                )}
                <InternalNameField ariaLabel={`${label} ${p.number} internal name`} value={f.id} saved={saved} hint="Used in exports and integrations, not shown to people filling in the form"
                  onChange={v => rename(i, v)} onBlur={() => settle(i)} onReset={typed.current.has(f.id) ? () => resetName(i) : undefined} />
              </>}
            </BuilderCard>
          );
        }} />
      {!hideAdd && !hasSections && addRow(rows.length - 1)}
    </div>
  );
}

/** A flat list of fields (no sections). */
export function FieldsBuilder({ fields, onChange, ...rest }: Omit<Parameters<typeof FieldRowsBuilder>[0], 'rows' | 'onChange' | 'nested'> & { fields: FormField[]; onChange: (f: FormField[]) => void }) {
  return <FieldRowsBuilder rows={rowsOf(fields)} onChange={r => onChange(splitRows(r).items)} {...rest} />;
}

/**
 * Sections, each with its own fields (handover templates, issue forms). t725u: built from the same rows as every other
 * builder; removing a section keeps its fields (they join the section above), and numbers run on across sections.
 */
export function SectionsBuilder({ sections, onChange, types, savedIds, selected, onSelect, hideAdd, typedIds }: {
  sections: FormSection[]; onChange: (s: FormSection[]) => void; types: readonly FormFieldType[]; savedIds: ReadonlySet<string>;
  /** The selected row (see rowsOfFormSections). */
  selected?: number | null; onSelect?: (i: number | null) => void; hideAdd?: boolean; typedIds?: MutableRefObject<Set<string>>;
}) {
  const rows = rowsOfFormSections(sections);
  const set = (r: BuilderRow<FormField>[]) => onChange(formSectionsOfRows(r));
  return (
    <div className="space-y-5">
      <FieldRowsBuilder rows={rows} onChange={set} types={types} savedIds={savedIds} selected={selected} onSelect={onSelect} hideAdd={hideAdd} typedIds={typedIds} nested />
      {!hideAdd && <Button variant="secondary" onClick={() => set(insertSectionRow(rows, null).rows)}><Plus className="h-4 w-4" />Add section</Button>}
    </div>
  );
}

/** Fill in a sectioned form. */
export function SectionedFormInput({ sections, values, onChange, errors, upload, labels, onLabel }: {
  sections: FormSection[]; values: FormValues; onChange: (v: FormValues) => void; errors?: Record<string, string>; upload?: FormUpload;
  labels?: Record<string, string>; onLabel?: (id: string, name: string) => void;
}) {
  return (
    <div className="space-y-5">
      {sections.map(s => (
        <fieldset key={s.id} className="space-y-2">
          {s.title && <legend className="text-sm font-bold text-slate-900 dark:text-slate-100">{s.title}</legend>}
          {s.description && <p className="text-xs text-slate-500">{s.description}</p>}
          <FormFieldsEditor fields={s.fields} values={values} onChange={onChange} errors={errors} upload={upload} labels={labels} onLabel={onLabel} />
        </fieldset>
      ))}
    </div>
  );
}

/** Read-only answers, grouped by section, in the version the record was made with. */
export function SectionedAnswers({ sections, values, renderFiles }: { sections: FormSection[]; values: FormValues; renderFiles?: (ids: string[]) => React.ReactNode }) {
  const show = (f: FormField) => {
    const v = values[f.id];
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)) return null;
    if (f.type === 'checkbox') return v ? 'Yes' : 'No';
    if (f.type === 'file') return renderFiles ? renderFiles(v as string[]) : `${(v as string[]).length} file(s)`;
    if (Array.isArray(v)) return v.join(', ');
    return `${String(v)}${f.unit ? ` ${f.unit}` : ''}`;
  };
  return (
    <div className="space-y-3">
      {sections.map(s => {
        const rows = s.fields.map(f => [f, show(f)] as const).filter(([, v]) => v !== null);
        if (!rows.length) return null;
        return (
          <div key={s.id}>
            {s.title && <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{s.title}</div>}
            <dl className="mt-1 grid grid-cols-[minmax(0,40%)_1fr] gap-x-3 gap-y-1 text-sm">
              {rows.map(([f, v]) => <div key={f.id} className="contents"><dt className="text-slate-500">{f.label}</dt><dd className="whitespace-pre-wrap">{v}</dd></div>)}
            </dl>
          </div>
        );
      })}
    </div>
  );
}

/** Initial values from every field's pre-filled value. */
export function sectionDefaults(sections: FormSection[]): FormValues {
  const out: FormValues = {};
  for (const f of sectionFields(sections)) if (f.default !== undefined && f.default !== null && f.default !== '') out[f.id] = f.default;
  return out;
}
